import { EMAIL_INGETION } from "../../Constants/Constants";
import AxiosInstance from "../../services/AxiosInstance";
import React, { useEffect, useState } from "react";
import { dummyEmailsData } from "../../mockData/emailQueueMockData";
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid2";
import EmailIcon from "@mui/icons-material/Email";
import DeleteIcon from "@mui/icons-material/Delete";
import AppTableView from "../AppTableView";
import { EMAIL_QUEUE_COLUMNS } from "../ticket/grid/GridDataConstants";
import TicketQueueDialog from "./TicketQueueDialog";
import Container from "react-bootstrap/Container";

const EmailQueue = ({ premissions }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [ignoreConfirmOpen, setIgnoreConfirmOpen] = useState(false);

  const fetchEmails = () => {
    return AxiosInstance.get(EMAIL_INGETION);
  };

  const saveEmail = (payload) => {
    return AxiosInstance.post(EMAIL_INGETION, payload);
  };

  const ignoreEmail = (messageId) => {
    return AxiosInstance.delete(`${EMAIL_INGETION}/${messageId}`);
  };

  useEffect(() => {
    loadEmails();
  }, []);

  const loadEmails = async () => {
    setLoading(true);
    try {
      const response = await fetchEmails();
      setEmails(response.data);
    } finally {
      setLoading(false);
    }
  };

  const handleIgnore = async (messageId) => {
    await ignoreEmail(messageId);
    setEmails((prev) => prev.filter((e) => e.messageId !== messageId));
  };

  const handleRowSelection = (ids) => {
    setSelectedIds(ids || []);
  };

  const selectedIdSet = new Set((selectedIds || []).map(String));
  const selectedEmails = emails.filter((e) =>
    selectedIdSet.has(String(e.messageId))
  );
  const singleSelectedEmail =
    selectedEmails.length === 1 ? selectedEmails[0] : null;
  const hasSelection = selectedIds.length > 0;

  const handleIgnoreSelected = async () => {
    setIgnoreConfirmOpen(false);
    for (const email of selectedEmails) {
      await ignoreEmail(email.messageId);
    }
    setEmails((prev) =>
      prev.filter((e) => !selectedIdSet.has(String(e.messageId)))
    );
    setSelectedIds([]);
  };

  return (
    <Container fluid>
      <Box
        sx={{
          padding: 2,
          paddingTop: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <Box display="flex" alignItems="center">
          <EmailIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight={600}>
            Incoming Mails
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <TicketQueueDialog
            callQueue={loadEmails}
            emailData={singleSelectedEmail}
            disabled={!singleSelectedEmail}
            tooltipTitle={
              singleSelectedEmail
                ? "Save as Ticket"
                : "Select one row to save as ticket"
            }
          />
          <Tooltip
            title={hasSelection ? "Ignore selected" : "Select rows to ignore"}
          >
            <span>
              <IconButton
                color="error"
                onClick={() => setIgnoreConfirmOpen(true)}
                disabled={!hasSelection}
                sx={{ border: "none", "&:focus": { outline: "none" } }}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Dialog
        open={ignoreConfirmOpen}
        onClose={() => setIgnoreConfirmOpen(false)}
        aria-labelledby="ignore-email-dialog-title"
        aria-describedby="ignore-email-dialog-description"
      >
        <DialogTitle id="ignore-email-dialog-title">
          Ignore selected email(s)?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="ignore-email-dialog-description">
            {selectedEmails.length === 1
              ? "This email will be ignored and removed from the queue. This action cannot be undone."
              : `Are you sure you want to ignore ${selectedEmails.length} selected emails? They will be removed from the queue and this action cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIgnoreConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleIgnoreSelected} color="error" variant="contained" autoFocus>
            Ignore
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2 }}>
          {loading ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="300px"
              mt={4}
              mb={4}
            >
              <CircularProgress size={48} thickness={4} color="primary" />
              <Box mt={2} color="text.secondary" fontSize={16}>
                Loading queue, please wait...
              </Box>
            </Box>
          ) : (
            <Grid gap={2} container spacing={2} columns={12}>
              <Grid size={{ xs: 12, lg: 12 }}>
                <AppTableView
                  idKey="messageId"
                  rows={emails}
                  cols={EMAIL_QUEUE_COLUMNS}
                  checkboxSelection
                  handleRowSelection={handleRowSelection}
                  hasRowSelectableFn={() => true}
                  paginationMode="client"
                  sortMode="client"
                />
              </Grid>
            </Grid>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default EmailQueue;
